/**
 * Light Controller Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * LightControllerSettingsPage (controller CRUD + channel editing).
 *
 * Stored procedures:
 *   getlightctrlsettings      — read controllers with nested channels & devices
 *   sp_dev_getareasbycampusid  — read campus areas (for add controller form)
 *   getsnfdevices              — read SNF devices (for channel device assignment)
 *
 * Tables:
 *   att_channel_types          — channel type definitions (32ch, 96ch Mono, 96ch RGB)
 *
 * Transaction types:
 *   lcbox_add    — add light controller (creates controller + all channel records)
 *   lcbox_edit   — edit channel (name, color, flashing, devices)
 *   lcbox_delete — delete light controller and all its channels
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch all light controllers (with nested channels & devices) for a campus. */
export function fetchLightControllers(campusId) {
    return apiFetch({
        procedure: 'getlightctrlsettings',
        reqType: 'stored',
        payload: { campus_id: campusId },
    });
}

/** Fetch channel type definitions (32ch, 96ch Mono, 96ch RGB). */
export function fetchChannelTypes() {
    return apiFetch({
        table: 'att_channel_types',
        procedure: 'fetchall',
        reqType: 'direct',
    });
}

/** Fetch areas for a campus (used in add controller form). */
export function fetchAreas(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getareasbycampusid',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch SNF devices for a campus (used in channel device assignment). */
export function fetchDevices(campusId) {
    return apiFetch({
        procedure: 'getsnfdevices',
        reqType: 'stored',
        payload: { campus_id: campusId },
    });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new light controller.
 * Backend creates the controller record + all channel records (32 or 96).
 */
export function addLightController(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'lcbox_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/**
 * Edit a channel (name, color, flashing, device assignment).
 */
export function editChannel(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'lcbox_edit',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/**
 * Delete a light controller and all its channels.
 */
export function deleteLightController(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'lcbox_delete',
        payload,
        campusId,
        userId,
        clientId,
    });
}
